import { normalizeVariableName } from '@mm/state/helpers/variable-names-helper';
import { Variable } from '@mm/state/interfaces/audiences';
import {
  containsTypes,
  myLeadsExcludeTypes,
  MyLeadsFilterTypes,
  MyLeadsFriendlyName,
  startsWithTypes,
  SubsectionKeys,
} from '@mm/state/interfaces/myLeads';
import {
  FilterItem,
  FilterItemOperatorTypes,
  FilterParams,
  FILTER_TYPES,
  TagFilterItem,
  TagsFilters,
} from '@mm/state/model/AudiencesModel';
import { isObject } from 'lodash';
import pluralize from 'pluralize';
import { PLATFORM_CAPTIONS } from '../Audiences/FilterBar/FilterBarPlatformFilter';
import {
  convertToCamesCase,
  convertToUpperSnakeCase,
} from '../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/helpers';
import { YES_VALUE } from '../GetLeads/LeadsSearch/LeadsSearchFiltersMenu/LeadsSearchFiltersMenu';
import { Tag } from './MyLeadsFilterBar/MyLeadsFilterBar';
import {
  getMinutesAndSeconds,
  remappedNames,
  setPlaceholderByKey,
  unassignFilterParams,
} from './MyLeadsFiltersPanel/helpers';

export const useMapAudienceFiltersToTags = () => {
  const mapQueryToTags = (
    filterParamObject,
    type: MyLeadsFilterTypes,
  ): Tag[] => {
    const excludeTag = myLeadsExcludeTypes.includes(type) ? 'Exclude' : '';

    return Object.entries(filterParamObject || {}).flatMap(
      ([key, values]: [string, (string | boolean)[]]) => {
        if (Array.isArray(values)) {
          return values.flatMap(value => {
            if (typeof value === 'string') {
              if (SubsectionKeys.location.includes(convertToCamesCase(key))) {
                return {
                  key,
                  value,
                  type,
                  fatTag: excludeTag,
                  friendlyName: value,
                };
              }
              if (
                key ===
                  convertToUpperSnakeCase(SubsectionKeys.landingPageUrl) ||
                key === SubsectionKeys.urlsVisited
              ) {
                const startsWithOperator = startsWithTypes.includes(type);
                const containsOperator = containsTypes.includes(type);
                const operator = startsWithOperator
                  ? ' starts with'
                  : containsOperator
                  ? ' contains'
                  : '';

                return {
                  key,
                  value,
                  type,
                  fatTag: `${excludeTag} ${MyLeadsFriendlyName[key]}${operator}`,
                  friendlyName: value,
                  lowercase: true,
                };
              }

              if (key === SubsectionKeys.tags) {
                return {
                  key,
                  value,
                  fatTag: `${excludeTag ? excludeTag : 'Include'} Tag`,
                  friendlyName: remappedNames(value).friendlyName,
                  type,
                };
              }

              return {
                key,
                value,
                friendlyName:
                  value === YES_VALUE.toLocaleLowerCase()
                    ? MyLeadsFriendlyName[key]
                    : remappedNames(value).friendlyName,
                type,
              };
            }
            if (typeof value === 'boolean') {
              if (key === convertToUpperSnakeCase(SubsectionKeys.leadType)) {
                return {
                  key,
                  value,
                  type,
                  friendlyName:
                    type === MyLeadsFilterTypes.excludedVariables
                      ? MyLeadsFriendlyName.CONSUMER_LEADS
                      : MyLeadsFriendlyName.BUSINESS_CONTACT,
                };
              }
            }
            if (typeof value === 'object') {
              const operator = isObject(value)
                ? Object.keys(value).map(
                    v => remappedNames(v).friendlyName,
                  )?.[0]
                : '';
              if (key === 'created_at') {
                return Object.entries(value)
                  .map(
                    ([dateKey, dateValue]: [string, string]) =>
                      ({
                        key,
                        subKey: dateKey,
                        value: dateValue,
                        friendlyName: `${
                          remappedNames(dateKey).friendlyName
                        }: ${dateValue}`,
                        type,
                      } as Tag),
                  )
                  .filter(i => i.value) as Tag[];
              }
              if (
                SubsectionKeys.customerJourney.includes(key) ||
                SubsectionKeys.revenueAttributes.includes(key)
              ) {
                const withOperator = operator ? ` ${operator}` : ':';
                const val = Object.values(value)?.[0];
                const formattedVal =
                  key === SubsectionKeys.customerJourney[0]
                    ? getMinutesAndSeconds(Number(val))
                    : val;

                return {
                  key,
                  value,
                  type,
                  friendlyName: `${
                    MyLeadsFriendlyName[key]
                  }${withOperator} ${formattedVal} ${
                    key !== SubsectionKeys.customerJourney[0]
                      ? setPlaceholderByKey(key)
                      : ''
                  }`,
                };
              }
              if (operator === FilterItemOperatorTypes.EXISTS) {
                const val = Object.values(value)?.[0];
                const friendlyName = val
                  ? `Variable ${normalizeVariableName(key)} exists`
                  : `Variable ${normalizeVariableName(key)} does not exist`;
                return {
                  key,
                  value,
                  type,
                  friendlyName,
                };
              }
            }

            return {
              key,
              value,
              friendlyName: MyLeadsFriendlyName[key] || key,
              type,
            };
          });
        }
        return [];
      },
    );
  };

  const mapFilterParamsToTags = (
    filterParams: FilterParams | undefined,
    audienceVariables: Variable[],
  ): Tag[] => {
    const formatedFilterParams = unassignFilterParams(
      filterParams,
    ) as FilterItem[];

    if (
      !filterParams ||
      !formatedFilterParams.length ||
      !formatedFilterParams?.every(f => f.value.length)
    ) {
      return [];
    }

    return formatedFilterParams?.map((filter: FilterItem) => {
      const isFilterByPlatform = filter.filterType === FILTER_TYPES.platforms;
      const variableName = audienceVariables.find(
        v => v.variableId === filter?.variableId,
      )?.friendlyName;
      const isPromoterIdFilter = filter.filterType === FILTER_TYPES.promoterId;

      return {
        key: filter.variableId || filter.filterType,
        value: Array.isArray(filter.value) ? filter.value.join() : filter.value,
        type: filter.filterType,
        friendlyName: Array.isArray(filter.value)
          ? filter.value
              .map(v => (isFilterByPlatform ? PLATFORM_CAPTIONS[v] : v))
              .join(', ')
          : isFilterByPlatform
          ? PLATFORM_CAPTIONS[filter.value]
          : filter.value,
        fatTag: `${
          isFilterByPlatform
            ? pluralize(filter.filterType, filter.value.length)
            : isPromoterIdFilter
            ? 'Trigger Id'
            : variableName
        }${
          !isFilterByPlatform && filter.operator
            ? ` ${filter.operator?.replace('_', ' ')}`
            : ''
        }`,
        lowercase: filter.filterType === 'variable_values',
      };
    });
  };

  const mapTagFiltersToTags = (tagFilters?: TagsFilters): Tag[] => {
    const tags = unassignFilterParams(tagFilters) as TagFilterItem[];
    return tags.map((tag: TagFilterItem) => {
      const tagsNames = tag.values.map(v => v.name);
      const formattedTag = remappedNames(tagsNames.join(', '));
      return {
        key: SubsectionKeys.tags,
        value: tagsNames,
        fatTag: `${tag.operator === 'exclude' ? 'Exclude' : 'Include'} Tag`,
        friendlyName: formattedTag.friendlyName,
        type: tag.filterType,
      };
    });
  };

  return { mapQueryToTags, mapFilterParamsToTags, mapTagFiltersToTags };
};
